import { DeleteOutlined } from '@ant-design/icons';
import { useView } from '@hooks';
import { Utils } from '@utils';
import { Button, Input, InputNumber, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import { ServiceExtraTypeSelect } from '../../Select/ServiceExtraTypeSelect';
import { IServiceExtraPresentation } from './ServiceExtraForm.props';

const { Text } = Typography;

const numericOnly = (e: React.KeyboardEvent) => {
  const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Enter'];
  if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (!/^[0-9.,]$/.test(e.key)) e.preventDefault();
};

const lbl: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  marginBottom: 2,
  display: 'block',
};

export const ServiceExtraPresentation: React.FC<IServiceExtraPresentation> = ({
  extra,
  disabled,
  price,
  costPrice,
  margin,
  quantity,
  discount,
  subTotal,
  vatAmount,
  total,
  handlePriceChange,
  handleCostPriceChange,
  handleMarginChange,
  handleQuantityChange,
  handleDiscountChange,
  handleQuantityBlur,
  handleNameChange,
  handleNumberChange,
  handleReferenceChange,
  handleTypeChange,
  handleRemove,
}) => {
  const { isSmallDesktop } = useView();
  const gridCols = isSmallDesktop
    ? '1fr 1fr 1fr 1fr 60px 55px 70px 60px 60px 72px 72px 86px 28px'
    : '1fr 1fr 1fr 1fr 70px 65px 80px 70px 70px 90px 90px 100px 32px';
  const gridGap = isSmallDesktop ? 6 : 8;
  return (
  <div style={{
    display: 'grid',
    gridTemplateColumns: gridCols,
    gap: gridGap, alignItems: 'end',
    padding: '6px 10px', marginBottom: 4,
  }}>
    {/* Name */}
    <div>
      <Text style={lbl}>Name</Text>
      <Input
        placeholder="Enter name"
        value={extra.name}
        disabled={disabled}
        onChange={(e: any) => handleNameChange(e.currentTarget.value)}
        status={extra.required && (!extra.name || !extra.name.trim().length) ? 'error' : undefined}
      />
    </div>

    {/* Part # (supplier) */}
    <div>
      <Text style={lbl}>Part #</Text>
      <Input
        placeholder="Part number"
        value={extra.number || ''}
        disabled={disabled}
        onChange={(e: any) => handleNumberChange(e.currentTarget.value)}
      />
    </div>

    {/* Reference (garage stock code) */}
    <div>
      <Text style={lbl}>Reference</Text>
      <Input
        placeholder="Stock code"
        value={extra.reference || ''}
        disabled={disabled}
        onChange={(e: any) => handleReferenceChange(e.currentTarget.value)}
      />
    </div>

    {/* Type */}
    <div>
      <Text style={lbl}>Type</Text>
      <ServiceExtraTypeSelect
        disabled={disabled}
        value={extra.type || 'Part'}
        onChange={(val) => handleTypeChange(val as string)}
      />
    </div>

    {/* Cost */}
    <div>
      <Text style={lbl}>Cost (£)</Text>
      <InputNumber min={0} step={0.01} precision={2} value={costPrice} disabled={disabled} onChange={handleCostPriceChange} onKeyDown={numericOnly} style={{ width: '100%' }} />
    </div>

    {/* Margin */}
    <div>
      <Text style={lbl}>Margin %</Text>
      <InputNumber min={0} step={0.01} precision={2} value={margin} disabled={disabled} onChange={handleMarginChange} onKeyDown={numericOnly} style={{ width: '100%' }} />
    </div>

    {/* Sale */}
    <div>
      <Text style={lbl}>Sale (£)</Text>
      <InputNumber min={0} step={0.01} precision={2} value={price} disabled={disabled} onChange={handlePriceChange} onKeyDown={numericOnly} style={{ width: '100%' }} />
    </div>

    {/* Qty */}
    {(() => {
      const availableStock = (extra as any).availableStock as number | undefined;
      const hasStockRef = typeof availableStock === 'number' && !!extra.inventoryId;
      const overStock = hasStockRef && (quantity ?? 0) > availableStock!;
      const stockTooltip = hasStockRef
        ? overStock
          ? `Only ${availableStock} in stock`
          : `${availableStock} in stock`
        : undefined;
      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <Text style={{ ...lbl, marginBottom: 0 }}>Qty</Text>
            {hasStockRef && (
              <Tooltip title={stockTooltip}>
                <span
                  style={{
                    fontSize: 10,
                    lineHeight: '14px',
                    padding: '0 6px',
                    borderRadius: 8,
                    background: overStock ? '#fff1f0' : '#f0f5ff',
                    color: overStock ? '#ff4d4f' : '#2f54eb',
                    border: `1px solid ${overStock ? '#ffccc7' : '#d6e4ff'}`,
                    fontWeight: 600,
                    cursor: 'help',
                  }}
                >
                  {availableStock}
                </span>
              </Tooltip>
            )}
          </div>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            value={quantity}
            disabled={disabled}
            onChange={handleQuantityChange}
            onBlur={handleQuantityBlur}
            onKeyDown={numericOnly}
            status={overStock ? 'error' : undefined}
            style={{ width: '100%' }}
          />
        </div>
      );
    })()}

    {/* Disc */}
    <div>
      <Text style={lbl}>Disc. %</Text>
      <InputNumber min={0} step={0.01} precision={2} value={discount} disabled={disabled} onChange={handleDiscountChange} onKeyDown={numericOnly} style={{ width: '100%' }} />
    </div>

    {/* Sub total */}
    <div style={{ paddingBottom: 4, textAlign: 'right' }}>
      <Text style={lbl}>Sub total</Text>
      <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{Utils.formatCurrency(subTotal)}</span>
    </div>

    {/* VAT */}
    <div style={{ paddingBottom: 4, textAlign: 'right' }}>
      <Text style={lbl}>VAT</Text>
      <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>{Utils.formatCurrency(vatAmount)}</span>
    </div>

    {/* Total */}
    <div style={{ paddingBottom: 4, textAlign: 'right' }}>
      <Text style={lbl}>Total</Text>
      <Tag color="blue" style={{ fontSize: 13, fontWeight: 700, padding: '2px 8px', margin: 0, minWidth: 70, textAlign: 'center', display: 'inline-block' }}>
        {Utils.formatCurrency(total)}
      </Tag>
    </div>

    {/* Delete */}
    <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 2 }}>
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        disabled={disabled}
        onClick={handleRemove}
      />
    </div>
  </div>
  );
};
